/** @jsx jsx */
import { Link, navigate } from 'gatsby'
import { jsx, Box } from 'theme-ui'
import React from 'react'

type SubmenuItem = {
  url: string
  title: string
}

type NavItem =
  | {
      url: string
      title: string
      hasSubmenu?: never
      visibleOnMobile?: boolean
    }
  | {
      title: string
      hasSubmenu: SubmenuItem[]
      url?: never
      visibleOnMobile?: boolean
    }

const desktopNavLinkSx = {
  fontSize: '15px',
  color: 'black',
  mr: '30px',
  position: 'relative',
  display: 'inline-flex',
  py: 2,
  textDecoration: 'none',
  ':hover': { color: '#22426f' },
  '::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 1,
    width: '100%',
    height: '2px',
    backgroundColor: '#22426f',
    transform: 'scaleX(0)',
    transformOrigin: 'center',
    transition: 'transform 180ms ease',
  },
  ':hover::after': {
    transform: 'scaleX(1)',
  },
  '@media screen and (max-width: 450px)': {
    mr: '15px',
  },
} as const

const desktopNavMenuLabelSx = {
  py: 2,
  position: 'relative',
  display: 'inline-flex',
  '::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 1,
    width: '100%',
    height: '2px',
    backgroundColor: '#22426f',
    transform: 'scaleX(0)',
    transformOrigin: 'center',
    transition: 'transform 180ms ease',
  },
} as const

// has not submenu {url: string, title: string, visibleOnMobile: boolean}
// has submenu {title: string, hasSubmenu: [{url: string, title: string}]}
const NAV_LIST: NavItem[] = [
  { url: '/project-list', title: '案件を探す', visibleOnMobile: true },
  { url: '/blog-details', title: 'お役立ちコンテンツ' },
  {
    title: 'フリーランス支援',
    hasSubmenu: [
      { url: '/freelance-start', title: 'フリーランス入門' },
      { url: '/consultation', title: '無料個別カウンセリング' },
      { url: '/diagnosis', title: '案件マッチング診断' },
      { url: '/value-check', title: '市場価値診断' },
      { url: '/skill-sheet', title: 'スキルシート対策' },
    ],
  },
  {
    title: 'お役立ち情報',
    hasSubmenu: [
      { url: '/glossary', title: 'IT用語集' },
      { url: '/interview-user', title: 'エンジニアインタビュー' },
      { url: '/coordinator', title: 'コーディネーター紹介' },
      { url: '/faq', title: 'FAQ' },
    ],
  },
  {
    title: '企業向け',
    hasSubmenu: [
      { url: '/for-companies', title: '人材をお探しの企業様' },
      { url: '/contact-company/', title: '人材について相談する' },
      { url: '/partners', title: 'パートナー企業・協業' },
    ],
  },
]

const MOBILE_NAV_LIST: NavItem[] = NAV_LIST

const normalizePath = (path: string): string =>
  path === '/' ? '/' : `/${path.split('/').filter(Boolean).join('/')}`

const Head = ({ pathname = '/' }: { pathname?: string }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const [openedMobileSubmenu, setOpenedMobileSubmenu] = React.useState<string | null>(null)
  const currentPath = normalizePath(pathname)

  const isUrlActive = (url: string): boolean => {
    const targetPath = normalizePath(url)

    if (targetPath === '/project-list') {
      return (
        currentPath.startsWith('/project-list') ||
        currentPath.startsWith('/project/')
      )
    }
    if (targetPath === '/blog-details') {
      return (
        currentPath.startsWith('/blog-details') ||
        currentPath.startsWith('/blog/')
      )
    }

    return (
      currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
    )
  }

  const isMenuActive = (item: NavItem): boolean => {
    if ('url' in item && item.url) return isUrlActive(item.url)
    if (!item.hasSubmenu) return false

    if (
      item.title === '企業向け' &&
      (currentPath === '/matching' || currentPath.startsWith('/schedule'))
    ) {
      return true
    }

    return item.hasSubmenu.some(({ url }) => isUrlActive(url))
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setOpenedMobileSubmenu(null)
  }

  const handleButtonClick = () => {
    navigate('/contact-engineer/')
    closeMobileMenu()
  }

  return (
    <div
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        backgroundColor: '#ffffff',
        width: '100%',
        margin: '0 auto',
        justifyContent: 'space-between',
        boxShadow: '0 5px 10px 0 rgba(0,0,0,.15)',
      }}
    >
      <header
        sx={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: ['0.5rem 16px', '0.5rem 24px', '0.5rem 32px'],
          height: ['60px', '80px'],
        }}
      >
        <Link to="/" aria-label="ホームへ">
          <img
            src="/new_logo_color.webp"
            alt="engibiz logo"
            sx={{
              width: ['130px', '160px'],
              maxWidth: '250px',
            }}
          />
        </Link>

        <nav
          aria-label="メインナビゲーション"
          role="navigation"
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-start',
            alignItems: 'center',
            fontSize: ['17px', '22px', '25px'],
            fontWeight: 500,
            marginLeft: ['16px', '32px'],
          }}
        >
          {
            NAV_LIST.map((value, index) => {
              const menuIsActive = isMenuActive(value)
              return (
                <Box key={index}>
                  {
                    value.hasSubmenu ? (
                      <Box
                        sx={{
                          position: 'relative',
                          marginRight: '30px',
                          cursor: 'pointer',
                          fontSize: '15px',
                          color: menuIsActive ? '#22426f' : 'black',
                          fontWeight: menuIsActive ? 700 : 500,
                          '.menu-label::after': {
                            transform: menuIsActive
                              ? 'scaleX(1)'
                              : 'scaleX(0)',
                          },
                          ':hover': {
                            color: '#22426f',
                          },
                          ':hover .menu-label::after': {
                            transform: 'scaleX(1)',
                          },
                          ':hover .submenu-list': {
                            opacity: 1,
                            visibility: 'visible',
                            transform: 'translate(-50%, 0)',
                          },
                          '@media screen and (max-width: 900px)': {
                            display: value.visibleOnMobile ? 'block' : 'none',
                          },
                        }}
                      >
                        <Box
                          className="menu-label"
                          sx={desktopNavMenuLabelSx}
                        >
                          {value.title}
                        </Box>
                        <Box
                          className="submenu-list"
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            minWidth: '180px',
                            backgroundColor: '#ffffff',
                            border: '1px solid rgb(162, 191, 232)',
                            borderRadius: 12,
                            boxShadow:
                              '0 24px 48px rgba(15,23,42,0.18), 0 10px 24px rgba(15,23,42,0.12)',
                            py: 2,
                            opacity: 0,
                            visibility: 'hidden',
                            transform: 'translate(-50%, 8px)',
                            transition: 'opacity 180ms ease, transform 180ms ease, visibility 180ms ease',
                            zIndex: 20,
                          }}
                        >
                          {value.hasSubmenu.map((submenu) => (
                            <Link
                              key={submenu.title}
                              to={submenu.url}
                              aria-current={
                                isUrlActive(submenu.url) ? 'page' : undefined
                              }
                              sx={{
                                display: 'block',
                                px: 3,
                                py: 2,
                                fontSize: '14px',
                                color: isUrlActive(submenu.url)
                                  ? '#22426f'
                                  : '#334155',
                                backgroundColor: isUrlActive(submenu.url)
                                  ? '#eef4fb'
                                  : 'transparent',
                                fontWeight: isUrlActive(submenu.url) ? 700 : 400,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                ':hover': {
                                  backgroundColor: '#f8fafc',
                                  color: '#22426f',
                                },
                              }}
                            >
                              {submenu.title}
                            </Link>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Link
                        to={value.url}
                        aria-current={menuIsActive ? 'page' : undefined}
                        sx={{
                          ...desktopNavLinkSx,
                          color: menuIsActive ? '#22426f' : 'black',
                          fontWeight: menuIsActive ? 700 : 500,
                          '::after': {
                            ...desktopNavLinkSx['::after'],
                            transform: menuIsActive
                              ? 'scaleX(1)'
                              : 'scaleX(0)',
                          },
                          '@media screen and (max-width: 900px)': {
                            display: value.visibleOnMobile
                              ? 'inline-flex'
                              : 'none',
                          },
                        }}
                      >
                        {value.title}
                      </Link>
                    )
                  }
                </Box>
              )
            })
          }
        </nav>

        <button
          type="button"
          aria-label="メニューを開く"
          aria-expanded={isMobileMenuOpen}
          onClick={() => {
            setIsMobileMenuOpen((prev) => {
              const next = !prev
              if (!next) {
                setOpenedMobileSubmenu(null)
              }
              return next
            })
          }}
          sx={{
            display: 'none',
            '@media screen and (max-width: 900px)': {
              display: 'inline-flex',
            },
            width: 44,
            height: 44,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '5px',
            border: 'none',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            ml: 'auto',
          }}
        >
          <Box
            as="span"
            sx={{
              width: 20,
              height: 2,
              backgroundColor: '#0f172a',
              transition: 'transform 180ms ease',
              transform: isMobileMenuOpen ? 'translateY(7px) rotate(45deg)' : 'none',
            }}
          />
          <Box
            as="span"
            sx={{
              width: 20,
              height: 2,
              backgroundColor: '#0f172a',
              opacity: isMobileMenuOpen ? 0 : 1,
              transition: 'opacity 180ms ease',
            }}
          />
          <Box
            as="span"
            sx={{
              width: 20,
              height: 2,
              backgroundColor: '#0f172a',
              transition: 'transform 180ms ease',
              transform: isMobileMenuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none',
            }}
          />
        </button>

        <div
          sx={{
            display: 'flex',
            alignItems: 'center',
            '@media screen and (max-width: 900px)': {
              display: 'none',
            },
          }}
        >
          <button
            type="button"
            onClick={handleButtonClick}
            sx={{
              border: '1px solid #f3af01',
              background: 'linear-gradient(90deg,#f2b100,#f28900)',
              color: '#fff',
              cursor: 'pointer',
              borderRadius: '999px',
              padding: '0.5rem 0.9rem',
              textAlign: 'center',
              boxShadow: '0 5px 10px rgba(0,0,0,0.12)',
              fontWeight: 700,
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            無料相談を申し込む
          </button>
        </div>
      </header>
      <Box
        sx={{
          '@media screen and (max-width: 900px)': {
            display: 'block',
          },
          display: 'none',
        }}
      >
        <Box
          onClick={closeMobileMenu}
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15,23,42,0.42)',
            opacity: isMobileMenuOpen ? 1 : 0,
            visibility: isMobileMenuOpen ? 'visible' : 'hidden',
            transition: 'opacity 220ms ease, visibility 220ms ease',
            zIndex: 100,
          }}
        />
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: 'min(86vw, 360px)',
            height: '100vh',
            backgroundColor: '#ffffff',
            boxShadow: '-16px 0 40px rgba(15,23,42,0.18)',
            transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 240ms ease',
            zIndex: 110,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: 3,
              py: 3,
              boxShadow: '0 3px 5px 0 rgba(0,0,0,.15)',
            }}
          >
            <button
              type="button"
              aria-label="メニューを閉じる"
              onClick={closeMobileMenu}
              sx={{
                width: 40,
                height: 40,
                border: 'none',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                fontSize: '20px',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Box
                as="span"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 18,
                  height: 2,
                  backgroundColor: '#0f172a',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                }}
              />
              <Box
                as="span"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 18,
                  height: 2,
                  backgroundColor: '#0f172a',
                  transform: 'translate(-50%, -50%) rotate(-45deg)',
                }}
              />
            </button>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3 }}>
            <Box sx={{ display: 'grid' }}>
              {MOBILE_NAV_LIST.map((value, index) => (
                <Box key={`${value.title}-${index}`} sx={{ borderBottom: '1px solid #d2d2d2', py: 3, px: 2 }}>
                  {value.hasSubmenu ? (
                    <Box
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        setOpenedMobileSubmenu((prev) =>
                          prev === value.title ? null : value.title,
                        )
                      }
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box
                          sx={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: isMenuActive(value)
                              ? '#22426f'
                              : '#0f172a',
                          }}
                        >
                          {value.title}
                        </Box>
                        <button
                          type="button"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: '#475569',
                            fontSize: '13px',
                            fontWeight: 700,
                            p: 0,
                          }}
                        >
                          <Box
                            as="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRight: '2px solid currentColor',
                              borderBottom: '2px solid currentColor',
                              transform:
                                openedMobileSubmenu === value.title
                                  ? 'rotate(-135deg)'
                                  : 'rotate(45deg)',
                              transition: 'transform 180ms ease',
                            }}
                          />
                        </button>
                      </Box>
                      {openedMobileSubmenu === value.title && (
                        <Box sx={{ display: 'grid', gap: 2, pt: 2 }}>
                          {value.hasSubmenu.map((submenu) => (
                            <Link
                              key={submenu.title}
                              to={submenu.url}
                              onClick={closeMobileMenu}
                              aria-current={
                                isUrlActive(submenu.url) ? 'page' : undefined
                              }
                              sx={{
                                display: 'block',
                                pl: 3,
                                fontSize: '14px',
                                color: isUrlActive(submenu.url)
                                  ? '#22426f'
                                  : '#475569',
                                fontWeight: isUrlActive(submenu.url) ? 700 : 400,
                                textDecoration: 'none',
                              }}
                            >
                              {submenu.title}
                            </Link>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Link
                      to={value.url || '/'}
                      onClick={closeMobileMenu}
                      aria-current={isMenuActive(value) ? 'page' : undefined}
                      sx={{
                        display: 'block',
                        fontSize: '15px',
                        fontWeight: 700,
                        color: isMenuActive(value) ? '#22426f' : '#0f172a',
                        textDecoration: 'none',
                      }}
                    >
                      {value.title}
                    </Link>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={handleButtonClick}
              sx={{
                border: '1px solid #f3af01',
                background: 'linear-gradient(90deg,#f2b100,#f28900)',
                color: '#fff',
                cursor: 'pointer',
                borderRadius: '999px',
                padding: '0.8rem 1rem',
                textAlign: 'center',
                boxShadow: '0 5px 10px rgba(0,0,0,0.12)',
                fontWeight: 700,
                fontSize: '0.95rem',
                width: '100%',
              }}
            >
              無料相談を申し込む
            </button>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default Head
